import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Itinerary } from '../../../models/itinerary';
import { GoogleMapComponent } from 'src/app/modules/shared/components/google.map/google.map.component';
import { SharedModule } from 'src/app/modules/shared/shared.module';
import { CommonModule } from '@angular/common';
import { of } from 'rxjs';
import { TripDay } from 'src/app/modules/shared/models/vititor.trip.storage.command';
import { TravelMode } from 'src/app/modules/shared/services/google.map.service';
import { ConfigService } from 'src/app/common/services/config/config.service';

@Component({
  selector: 'ig-direction-navigation',
  standalone: true,
  imports: [SharedModule, CommonModule],
  templateUrl: './direction.navigation.component.html',
  styleUrl: './direction.navigation.component.scss',
})
export class DirectionNavigationComponent {
  @Input() currentItinerary: Itinerary | TripDay;
  @Input() map: GoogleMapComponent;
  @Input() directionResults: google.maps.DirectionsResult;
  @Input() travelMode: TravelMode = TravelMode.DRIVING;
  @Input() mode: 'light' | 'dark' = 'light';

  noMoreNext = false;
  noMorePrevious = true;
  bigStopIndex = 0;
  smallStepIndex = 0;

  direction = 'Getting directions...';
  time = '';
  distance = '';

  isStarted = false;

  @Output() onExit: EventEmitter<any> = new EventEmitter();

  nextLeg(isGoToNext: boolean = true) {
    this.isStarted = true;
    const directionSegment = this.getDirectionSegment(isGoToNext);
    this.setDirectionText();
    if (directionSegment) {
      this.map?.drawDirectionRoute(directionSegment, this.travelMode);
    }
  }

  navigateFromUserGPSLocation(travelMode?: TravelMode) {
    let waypoints = [];
    let destination = '';

    for (let i = 0; i < this.currentItinerary.attractions.length; i++) {
      const x = this.currentItinerary.attractions[i];
      if (x.item.address) {
        let address = x.item.address.address;
        address = address.replace(/ /g, '+');
        address = address.replace(/null,/g, '');

        if (i === this.currentItinerary.attractions.length - 1) {
          destination = address;
        } else {
          waypoints.push(address);
        }
      }
    }

    let stops = waypoints.join('|');

    let navigationUrl = ConfigService.config.google.navigationApiShort.replace(
      '${waypoints}',
      stops
    );
    navigationUrl = navigationUrl.replace(
      '${mode}',
      travelMode?.toLowerCase() ||
        this.currentItinerary['defaultTravelMode']?.mode?.toLowerCase() ||
        'driving'
    );
    navigationUrl = navigationUrl.replace('${destination}', destination);
    console.log('navigationUrl', navigationUrl);
    localStorage.setItem('gps-url', navigationUrl);

    window.open(
      `${ConfigService.config.domainV2}gps-redirect/${encodeURIComponent(
        navigationUrl
      )}`,
      '_blank'
    );
  }

  exit() {
    if (this.isStarted) {
      this.map.directionsResults$ = of(this.directionResults);
      this.bigStopIndex = 0;
      this.smallStepIndex = 0;
      this.time = '';
      this.distance = '';
      this.direction = 'Getting directions...';
      this.noMoreNext = false;
      this.noMorePrevious = true;
      this.onExit.emit();
      this.isStarted = false;
    }
  }

  public previous() {
    if (this.noMorePrevious) {
      return;
    }

    let segmentChanged = false;
    if (this.smallStepIndex > 0) {
      this.smallStepIndex--;
      if (this.smallStepIndex === 0 && this.bigStopIndex === 0) {
        this.noMorePrevious = true;
      }
    } else {
      if (this.bigStopIndex > 0) {
        this.bigStopIndex--;
        segmentChanged = true;
        this.smallStepIndex =
          this.directionResults.routes[0].legs[this.bigStopIndex].steps.length -
          1;
        if (this.bigStopIndex === 0 && this.smallStepIndex === 0) {
          this.noMorePrevious = true;
        }
      } else {
        this.noMorePrevious = true;
        return;
      }
    }
    this.noMoreNext = false;
    if (segmentChanged) {
      this.nextLeg(false);
    } else {
      this.setDirectionText();
    }
  }

  public next() {
    if (this.noMoreNext) {
      return;
    }

    let segmentChanged = false;
    if (
      this.smallStepIndex <
      this.directionResults.routes[0].legs[this.bigStopIndex].steps.length - 1
    ) {
      this.smallStepIndex++;
      if (
        this.bigStopIndex === this.directionResults.routes[0].legs.length - 1 &&
        this.smallStepIndex ===
          this.directionResults.routes[0].legs[this.bigStopIndex].steps.length -
            1
      ) {
        this.noMoreNext = true;
      }
    } else {
      this.smallStepIndex = 0;
      if (this.bigStopIndex < this.directionResults.routes[0].legs.length - 1) {
        this.bigStopIndex++;
        segmentChanged = true;
        if (
          this.bigStopIndex ===
            this.directionResults.routes[0].legs.length - 1 &&
          this.smallStepIndex ===
            this.directionResults.routes[0].legs[this.bigStopIndex].steps
              .length -
              1
        ) {
          this.noMoreNext = true;
        }
      } else {
        this.noMoreNext = true;
        return;
      }
    }
    this.noMorePrevious = false;
    if (segmentChanged) {
      this.nextLeg();
    } else {
      this.setDirectionText();
    }
  }

  private setDirectionText() {
    const leg = this.directionResults.routes[0].legs[this.bigStopIndex];
    this.direction = leg.steps[this.smallStepIndex].instructions;
    this.time = leg.steps[this.smallStepIndex].duration.text;
    this.distance = leg.steps[this.smallStepIndex].distance.text;
  }

  private getDirectionSegment(isNext: boolean) {
    let start = 0;
    let stop = 0;
    if (isNext) {
      if (this.bigStopIndex >= this.currentItinerary.attractions.length - 1) {
        return null;
      }
      start = this.bigStopIndex;
      stop = this.bigStopIndex + 1;
    } else {
      if (this.bigStopIndex < 0) {
        return null;
      }
      start = this.bigStopIndex;
      stop = this.bigStopIndex + 1;
    }

    let startLat = this.currentItinerary.attractions[start].item.address.lat;
    let startLng = this.currentItinerary.attractions[start].item.address.lng;
    let stopLat = this.currentItinerary.attractions[stop].item.address.lat;
    let stopLng = this.currentItinerary.attractions[stop].item.address.lng;

    return [
      {
        lat: startLat,
        lng: startLng,
        stopover: true,
      },
      {
        lat: stopLat,
        lng: stopLng,
        stopover: true,
      },
    ];
  }
}
